import type { ListGamesResponse } from "@/app/(api)/api/listGames/listGames.js";
import { listGames } from "@/app/(api)/api/listGames/listGames.js";
import type { QueryClient, InfiniteData } from "@tanstack/react-query";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import type { APIGame, APIUser } from "@rosette/ts/src/business/api/api_schema.js";
import { useEffect } from "react";
import { cacheGame } from "@/app/(api)/api/getGame/useGame.js";


const PAGE_LIMIT = 10;


export interface UseInfiniteGamesResult {
    gamePages: APIGame[][] | undefined;
    loadingMoreGames: boolean;
    loadMoreGames: () => Promise<unknown>;
    canLoadMoreGames: boolean;
    gamesError: Error | null;
}


export function useInfiniteGames(user: APIUser): UseInfiniteGamesResult {
    const queryClient = useQueryClient();
    const {
        data,
        isFetchingNextPage,
        fetchNextPage,
        hasNextPage,
        error,
    } = useInfiniteQuery<
        ListGamesResponse, Error, InfiniteData<ListGamesResponse>,
        ["infiniteGames", string], string | null
    >({
        queryKey: ["infiniteGames", user.publicID],
        queryFn: ({ pageParam }) => {
            return listGames(user.publicID, pageParam, PAGE_LIMIT);
        },
        initialPageParam: null,
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.games || lastPage.endOfGames)
                return undefined;

            const games = lastPage.games;
            return games[games.length - 1].publicID;
        },
    });

    let gamePages: APIGame[][] | undefined = undefined;
    if (data) {
        gamePages = [];
        for (const page of data.pages) {
            gamePages.push(page.games);
        }
    }

    // Cache games so we don't load them again when
    // redirecting to view more details about them.
    useEffect(() => {
        if (gamePages) {
            for (const page of gamePages) {
                for (const game of page) {
                    cacheGame(queryClient, game);
                }
            }
        }
    }, [queryClient, gamePages]);

    return {
        gamePages: gamePages,
        loadingMoreGames: isFetchingNextPage,
        loadMoreGames: fetchNextPage,
        canLoadMoreGames: hasNextPage,
        gamesError: error,
    };
}


export function invalidateInfiniteGames(queryClient: QueryClient) {
    queryClient.removeQueries({ queryKey: ["infiniteGames"] });
}
